import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Badge, Spinner, Nav } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bugsAPI, projectsAPI } from '../services/api';
import { format } from 'date-fns';
import { useAuth } from '../utils/AuthContext';

function BugsList() {
  const [bugs, setBugs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'my'
  const [filters, setFilters] = useState({
    project_id: '',
    status: '',
    priority: '',
    bug_type: ''
  });
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
    fetchBugs();
  }, []);

  useEffect(() => {
    fetchBugs();
  }, [filters, activeTab]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects', error);
    }
  };

  const fetchBugs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.bug_type) params.bug_type = filters.bug_type;

      // Filter by assigned user when "My Bugs" tab is active
      if (activeTab === 'my' && user?.id) {
        params.assigned_to = user.id;
      }

      const response = await bugsAPI.getBugs(params);
      setBugs(response.data);
    } catch (error) {
      toast.error('Failed to load bugs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const clearFilters = () => {
    setFilters({ project_id: '', status: '', priority: '', bug_type: '' });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Open': 'danger',
      'In Progress': 'primary',
      'Resolved': 'success',
      'Closed': 'secondary',
      'Re-opened': 'warning',
      'Not a Bug': 'info',
      'Cannot Reproduce': 'secondary',
      'Pending QA': 'warning'
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Critical': 'danger',
      'High': 'warning',
      'Medium': 'primary',
      'Low': 'secondary'
    };
    return <Badge bg={priorityColors[priority] || 'secondary'}>{priority}</Badge>;
  };

  const getBugTypeBadge = (bugType) => {
    const typeColors = {
      'Bug': 'danger',
      'Feature': 'success',
      'Enhancement': 'info',
      'Ticket': 'warning'
    };
    return <Badge bg={typeColors[bugType] || 'secondary'}>{bugType}</Badge>;
  };

  return (
    <Container fluid style={{ padding: '2rem 3rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold mb-0" style={{ fontSize: '28px', color: '#1a1a1a' }}>Bugs</h2>
        <Button
          variant="primary"
          onClick={() => navigate('/bugs/new')}
          style={{
            borderRadius: '8px',
            padding: '10px 20px',
            fontWeight: '600',
            fontSize: '14px',
            border: 'none'
          }}
        >
          + Create Bug
        </Button>
      </div>

      {/* Tabs */}
      <Nav variant="tabs" className="mb-3" style={{ borderBottom: '2px solid #e0e0e0' }}>
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'all'}
            onClick={() => setActiveTab('all')}
            style={{
              cursor: 'pointer',
              fontWeight: activeTab === 'all' ? '600' : '500',
              fontSize: '14px',
              color: activeTab === 'all' ? '#0066cc' : '#666',
              borderColor: activeTab === 'all' ? '#0066cc' : 'transparent',
              borderWidth: activeTab === 'all' ? '0 0 2px 0' : '0',
              borderStyle: 'solid',
              marginBottom: '-2px',
              padding: '10px 20px',
              backgroundColor: 'transparent'
            }}
          >
            All Bugs
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'my'}
            onClick={() => setActiveTab('my')}
            style={{
              cursor: 'pointer',
              fontWeight: activeTab === 'my' ? '600' : '500',
              fontSize: '14px',
              color: activeTab === 'my' ? '#0066cc' : '#666',
              borderColor: activeTab === 'my' ? '#0066cc' : 'transparent',
              borderWidth: activeTab === 'my' ? '0 0 2px 0' : '0',
              borderStyle: 'solid',
              marginBottom: '-2px',
              padding: '10px 20px',
              backgroundColor: 'transparent'
            }}
          >
            My Bugs
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
        <Card.Body style={{ padding: '1.25rem' }}>
          <Row className="g-3">
            <Col md={3}>
              <Form.Select
                name="project_id"
                value={filters.project_id}
                onChange={handleFilterChange}
                style={{
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  border: '1px solid #e0e0e0'
                }}
              >
                <option value="">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                style={{
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  border: '1px solid #e0e0e0'
                }}
              >
                <option value="">All Status</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
                <option value="Re-opened">Re-opened</option>
                <option value="Not a Bug">Not a Bug</option>
                <option value="Cannot Reproduce">Cannot Reproduce</option>
                <option value="Pending QA">Pending QA</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                name="priority"
                value={filters.priority}
                onChange={handleFilterChange}
                style={{
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  border: '1px solid #e0e0e0'
                }}
              >
                <option value="">All Priority</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                name="bug_type"
                value={filters.bug_type}
                onChange={handleFilterChange}
                style={{
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  border: '1px solid #e0e0e0'
                }}
              >
                <option value="">All Types</option>
                <option value="Bug">Bug</option>
                <option value="Feature">Feature</option>
                <option value="Enhancement">Enhancement</option>
                <option value="Ticket">Ticket</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                onClick={clearFilters}
                className="w-100"
                style={{
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontWeight: '500',
                  fontSize: '14px',
                  border: '1px solid #e0e0e0'
                }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Bugs Table */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
        <Card.Body style={{ padding: '1.5rem' }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <tr>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Bug ID</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Title</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Project</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Type</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Priority</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Status</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Assignee</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Reporter</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Created</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {bugs.length > 0 ? (
                    bugs.map((bug) => (
                      <tr
                        key={bug.id}
                        style={{
                          cursor: 'pointer',
                          borderBottom: '1px solid #f5f5f5'
                        }}
                        onClick={() => navigate(`/bugs/${bug.id}`)}
                      >
                        <td style={{ fontSize: '14px', padding: '12px 8px' }}>
                          <strong style={{ color: '#0066cc' }}>{bug.bug_number}</strong>
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#333' }}>{bug.title}</td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{bug.project_name}</td>
                        <td style={{ padding: '12px 8px' }}>{getBugTypeBadge(bug.bug_type)}</td>
                        <td style={{ padding: '12px 8px' }}>{getPriorityBadge(bug.priority)}</td>
                        <td style={{ padding: '12px 8px' }}>{getStatusBadge(bug.status)}</td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{bug.assignee_name || '-'}</td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{bug.reporter_name}</td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#999' }}>{format(new Date(bug.created_at), 'MMM dd, yyyy')}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <Badge bg="secondary" style={{ borderRadius: '12px', padding: '4px 10px', fontSize: '12px', fontWeight: '500' }}>{bug.comment_count}</Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center text-muted" style={{ padding: '24px' }}>
                        No bugs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default BugsList;
